import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import {
  Box,
  Typography,
  Grid,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { base_url, getToken } from '../../config/config';

// Default colors for fallback
const defaultColors = {
  primary: {
    100: '#d0d1d5',
    200: '#a1a4ab',
    300: '#727681',
    400: '#1F2A40',
    500: '#141b2d',
  },
  grey: {
    100: '#e0e0e0',
    200: '#c2c2c2',
    300: '#a3a3a3',
    400: '#858585',
    500: '#666666',
    600: '#4d4d4d',
    700: '#333333',
    800: '#1a1a1a',
    900: '#0a0a0a',
  },
  greenAccent: {
    100: '#dbf5ee',
    200: '#b7ebde',
    300: '#94e2cd',
    400: '#70d8bd',
    500: '#4cceac',
    600: '#3da58a',
    700: '#2e7c67',
    800: '#1e5245',
    900: '#0f2922',
  },
  redAccent: {
    100: '#f8dcdb',
    200: '#f1b9b7',
    300: '#e99592',
    400: '#e2726e',
    500: '#db4f4a',
    600: '#af3f3b',
    700: '#832f2c',
    800: '#58201e',
    900: '#2c100f',
  },
  blueAccent: {
    100: '#e1e2fe',
    200: '#c3c6fd',
    300: '#a4a9fc',
    400: '#868dfb',
    500: '#6870fa',
    600: '#535ac8',
    700: '#3e4396',
    800: '#2a2d64',
    900: '#151632',
  },
  orange: {
    400: '#FB8C00',
    500: '#EF6C00',
  }
};

const ReklamationStats = () => {
  const theme = useTheme();
  let colors;
  try {
    colors = tokens(theme.palette.mode);
  } catch (error) {
    console.warn("Theme error, using fallback colors", error);
    colors = defaultColors;
  }
  const token = getToken();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    statusCounts: {},
    priorityCounts: {},
    processTypeCounts: {},
    monthlyStats: [],
    topRecipients: [],
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${base_url}/v1/getReklamationStats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching reklamation stats:', err);
      setError('Fehler beim Laden der Statistiken. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Prepare data for charts
  const statusData = Object.entries(stats.statusCounts).map(([status, count]) => ({
    name: status === 'open' ? 'Offen' : status === 'in-progress' ? 'In Bearbeitung' : 'Abgeschlossen',
    value: count,
    color: status === 'open' ? colors.redAccent[500] : status === 'in-progress' ? colors.blueAccent[500] : colors.greenAccent[500],
  }));

  const priorityData = Object.entries(stats.priorityCounts).map(([priority, count]) => ({
    name: priority === 'high' ? 'Hoch' : priority === 'medium' ? 'Mittel' : 'Niedrig',
    value: count,
    color: priority === 'high' ? colors.redAccent[500] : priority === 'medium' ? colors.orange[500] : colors.greenAccent[500],
  }));

  const processTypeData = Object.entries(stats.processTypeCounts).map(([type, count]) => ({
    name: type === 'external' ? 'Extern' : 'Intern',
    value: count,
    color: type === 'external' ? colors.blueAccent[500] : colors.greenAccent[500],
  }));

  // Prepare monthly data for line chart
  const monthlyData = stats.monthlyStats.map(item => {
    const month = item._id.month;
    const year = item._id.year;
    const monthNames = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
    
    return {
      month: `${monthNames[month - 1]} ${year}`,
      count: item.count,
    };
  });

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          width: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
          width: '100%',
        }}
      >
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Accordion
      defaultExpanded
      sx={{
        backgroundColor: colors.primary[400],
        mb: 3,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{ borderBottom: `1px solid ${colors.primary[300]}` }}
      >
        <Typography variant="h5" fontWeight="bold">
          Reklamationsstatistiken
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2}>
          {/* Status Distribution */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: colors.primary[500],
                height: '100%',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                textAlign="center"
                mb={1}
              >
                Status-Verteilung
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    contentStyle={{
                      backgroundColor: colors.primary[300],
                      borderColor: colors.primary[200],
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Priority Distribution */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: colors.primary[500],
                height: '100%',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                textAlign="center"
                mb={1}
              >
                Prioritäts-Verteilung
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={priorityData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {priorityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    contentStyle={{
                      backgroundColor: colors.primary[300],
                      borderColor: colors.primary[200],
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Process Type Distribution */}
          <Grid item xs={12} md={4}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: colors.primary[500],
                height: '100%',
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                textAlign="center"
                mb={1}
              >
                Prozesstyp-Verteilung
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={processTypeData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent }) =>
                      `${name}: ${(percent * 100).toFixed(0)}%`
                    }
                  >
                    {processTypeData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value, name) => [value, name]}
                    contentStyle={{
                      backgroundColor: colors.primary[300],
                      borderColor: colors.primary[200],
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Monthly Trend */}
          <Grid item xs={12}>
            <Paper
              sx={{
                p: 2,
                backgroundColor: colors.primary[500],
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                textAlign="center"
                mb={1}
              >
                Monatliche Reklamationen (letzten 6 Monate)
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart
                  data={monthlyData}
                  margin={{
                    top: 5,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[600]} />
                  <XAxis
                    dataKey="month"
                    stroke={colors.grey[300]}
                    tick={{ fill: colors.grey[300] }}
                  />
                  <YAxis
                    stroke={colors.grey[300]}
                    tick={{ fill: colors.grey[300] }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: colors.primary[300],
                      borderColor: colors.primary[200],
                      color: colors.grey[100],
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="count"
                    name="Anzahl Reklamationen"
                    stroke={colors.blueAccent[400]}
                    activeDot={{ r: 8 }}
                    strokeWidth={2}
                  />
                </LineChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default ReklamationStats;